/**
 * Composant d'affichage des résultats - Calculatrice AEP
 */

import React from 'react';
import { Calculator, Info, Euro, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { formatNumber, formatPrice } from '../../lib/aep-calculations';
import { PRESSION_AVAL_MIN, MODES_DUREE } from '../../lib/aep-constants';

export const ResultsCard = ({ 
  resultats, 
  mode, 
  dureeHeures, 
  pressionAmont, 
  pressionExploitable, 
  debit, 
  unite, 
  rendement 
}) => {
  return (
    <Card 
      className="bg-[#1E3A8A] border-0 rounded-sm shadow-none"
      data-testid="results-card"
    >
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Résultats de calcul
        </CardTitle>
        <CardDescription className="text-[#93C5FD] text-sm">
          Mode: {MODES_DUREE[mode].label} ({formatNumber(dureeHeures, 0)} heures)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Pressions */}
        <div className="grid grid-cols-3 gap-2 bg-[#172554]/50 rounded-sm p-3">
          <div className="text-center">
            <div className="text-[#93C5FD] text-xs uppercase">Amont</div>
            <div className="text-xl font-semibold text-white font-mono-data">{pressionAmont} bar</div>
          </div>
          <div className="text-center border-x border-[#3B82F6]/30">
            <div className="text-amber-300 text-xs uppercase">Aval min.</div>
            <div className="text-xl font-semibold text-amber-300 font-mono-data">{PRESSION_AVAL_MIN} bar</div>
          </div>
          <div className="text-center">
            <div className="text-[#6EE7B7] text-xs uppercase">Exploitable</div>
            <div className={`text-xl font-semibold font-mono-data ${pressionExploitable > 0 ? 'text-[#6EE7B7]' : 'text-red-400'}`}>
              {pressionExploitable} bar
            </div>
          </div>
        </div>

        {/* Alerte si pression non exploitable */}
        {pressionExploitable === 0 && (
          <div className="bg-red-500/20 border border-red-400/50 rounded-sm p-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <span className="text-red-200 text-sm">
              Pression insuffisante - Aucune énergie récupérable
            </span>
          </div>
        )}
        
        {/* Puissance théorique */}
        <div className="bg-[#172554]/50 rounded-sm p-4">
          <div className="text-[#93C5FD] text-xs uppercase tracking-wider mb-1">
            Puissance théorique
          </div>
          <div className="flex items-baseline gap-3">
            <span 
              className="text-3xl font-semibold text-white font-mono-data"
              data-testid="puissance-theorique-kw"
            >
              {formatNumber(resultats.puissanceTheoriqueKW)}
            </span>
            <span className="text-[#93C5FD]">kW</span>
            <span className="text-sm text-[#93C5FD] font-mono-data">
              ({formatNumber(resultats.puissanceTheoriqueW)} W)
            </span>
          </div>
        </div>

        {/* Puissance utile */}
        <div className="bg-[#059669]/30 rounded-sm p-4 border border-[#059669]/50">
          <div className="text-[#6EE7B7] text-xs uppercase tracking-wider mb-1 flex items-center gap-2">
            Puissance utile
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Puissance théorique × rendement ({rendement}%)</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-baseline gap-3">
            <span 
              className="text-3xl font-semibold text-white font-mono-data"
              data-testid="puissance-utile-kw"
            >
              {formatNumber(resultats.puissanceUtileKW)}
            </span>
            <span className="text-[#6EE7B7]">kW</span>
            <span className="text-sm text-[#6EE7B7] font-mono-data">
              ({formatNumber(resultats.puissanceUtileW)} W)
            </span>
          </div>
        </div>

        <Separator className="bg-[#3B82F6]/30" />

        {/* Énergies */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#172554]/50 rounded-sm p-4">
            <div className="text-[#93C5FD] text-xs uppercase tracking-wider mb-1">
              Énergie théorique
            </div>
            <div className="flex flex-col">
              <span 
                className="text-2xl font-semibold text-white font-mono-data"
                data-testid="energie-theorique-kwh"
              >
                {formatNumber(resultats.energieTheoriqueKWh)}
              </span>
              <span className="text-[#93C5FD] text-sm">kWh</span>
            </div>
          </div>
          <div className="bg-[#059669]/30 rounded-sm p-4 border border-[#059669]/50">
            <div className="text-[#6EE7B7] text-xs uppercase tracking-wider mb-1">
              Énergie utile
            </div>
            <div className="flex flex-col">
              <span 
                className="text-2xl font-semibold text-white font-mono-data"
                data-testid="energie-utile-kwh"
              >
                {formatNumber(resultats.energieUtileKWh)}
              </span>
              <span className="text-[#6EE7B7] text-sm">kWh</span>
            </div>
          </div>
        </div>

        <Separator className="bg-[#3B82F6]/30" />

        {/* Revenus potentiels */}
        <div className="space-y-3">
          <div className="text-white text-sm font-medium flex items-center gap-2">
            <Euro className="h-4 w-4" />
            Revenus potentiels ({MODES_DUREE[mode].label.toLowerCase()})
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#172554]/50 rounded-sm p-4">
              <div className="text-[#93C5FD] text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
                Vente EDF
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3 w-3" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tarif: 0,04 €/kWh</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex flex-col">
                <span 
                  className="text-xl font-semibold text-white font-mono-data"
                  data-testid="revenus-vente-edf"
                >
                  {formatPrice(resultats.revenusVenteEDF)}
                </span>
                <span className="text-[#93C5FD] text-xs">à 0,04 €/kWh</span>
              </div>
            </div>
            <div className="bg-[#059669]/30 rounded-sm p-4 border border-[#059669]/50">
              <div className="text-[#6EE7B7] text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
                Autoconsommation
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3 w-3" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tarif: 0,19 €/kWh</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex flex-col">
                <span 
                  className="text-xl font-semibold text-white font-mono-data"
                  data-testid="revenus-autoconsommation"
                >
                  {formatPrice(resultats.revenusAutoconsommation)}
                </span>
                <span className="text-[#6EE7B7] text-xs">à 0,19 €/kWh</span>
              </div>
            </div>
          </div>
        </div>

        {/* Récapitulatif des paramètres */}
        <div className="bg-[#172554]/30 rounded-sm p-3 mt-4">
          <div className="text-xs text-[#93C5FD] flex flex-wrap gap-x-4 gap-y-1 font-mono-data">
            <span>P. amont: {pressionAmont} bar</span>
            <span>P. exploit.: {pressionExploitable} bar</span>
            <span>Débit: {debit} {unite === 'lmin' ? 'L/min' : 'm³/h'}</span>
            <span>Rendement: {rendement}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResultsCard;
